import { Check, TrendingUp, TriangleAlert, XCircle } from 'lucide-react'
import React, { useMemo } from 'react'

import { TextLink } from 'slab'
import { conditionallyLocalizeDate } from 'utils'
import type { MembersEnrollmentOverviewProgressState } from 'utils/web_api.schemas'

import { snakeCaseToSentenceCase } from '../../utils'

type EnrollmentProgressProps =
  // When showCaption is explicitly false, no additional fields are required regardless of state
  | {
    state: MembersEnrollmentOverviewProgressState
    showCaption: false
    startDate?: Date
    children?: React.ReactNode
  }
  // When showCaption is true or undefined (defaults to true), requirements depend on state
  | {
    state: 'on_track' | 'enrolled'
    showCaption?: true
    startDate: Date
    children?: React.ReactNode
  }
  | {
    state: 'needs_attention' | 'failed'
    showCaption?: true
    startDate?: Date
    children?: React.ReactNode
  }

const EnrollmentProgress: React.FC<EnrollmentProgressProps> = ({
  state,
  startDate,
  showCaption = true,
  children
}) => {
  const { color, IconComponent, caption } = useMemo(() => {
    switch (state) {
      case 'needs_attention':
        // Render the children if provided to support custom captions (e.g., "Complete this Docusign to continue enrollment")
        return {
          color: 'orange',
          IconComponent: TriangleAlert,
          caption: children || (
            <>We need additional info from you before we can submit. Check your inbox for an email from{' '}<TextLink href='mailto:broker@thatch.com'>broker@thatch.com</TextLink></>
          )
        }
      case 'failed':
        return {
          color: 'red',
          IconComponent: XCircle,
          caption: (
            <>We're looking into why your enrollment didn't go through, but you can reach out to{' '}<TextLink href='mailto:support@thatch.com'>support@thatch.com</TextLink>{' '}for updates</>
          )
        }
      case 'enrolled':
        return {
          color: 'green',
          IconComponent: Check,
          caption: startDate && `Coverage will start ${conditionallyLocalizeDate(startDate)}`
        }
      case 'on_track':
      default:
        return {
          color: 'green',
          IconComponent: TrendingUp,
          caption: startDate && `Everything looks good to have you covered on ${conditionallyLocalizeDate(startDate)}`
        }
    }
  }, [state, startDate, children])

  return (
    <div className='flex flex-col'>
      <div className='flex items-center gap-2'>
        <div className={`w-5 h-5 rounded-full bg-${color}-50 flex items-center justify-center`}>
          <IconComponent size={12} className={`text-${color}-400`} />
        </div>
        <span className={`body text-${color}-500`}>{snakeCaseToSentenceCase(state)}</span>
      </div>
      {showCaption && (
        <div className='mt-2 body-sm text-gray-500'>
          {caption}
        </div>
      )}
    </div>
  )
}

export default EnrollmentProgress
